import { IErrorMessage } from '@core/interface/error-message.interface';

/**
 * Traducciones de los códigos de error de negocio que envía el backend
 * (campo `code` del ApiResponse). El backend maneja códigos estables y mensajes
 * técnicos en inglés; el texto para el usuario vive únicamente aquí.
 *
 * Si un código no está aquí, el interceptor usa el mensaje técnico del backend
 * como fallback — agregar el código lo traduce.
 */
export const ERROR_MESSAGES: Record<string, IErrorMessage> = {
  ORGANIZATION_COMPANY_HOLDING_LOCKED: {
    title: 'Holding no modificable',
    subtitle:
      'El holding de una empresa no se puede cambiar una vez asignado, para no mover a sus usuarios de grupo.',
  },
  ACTIVITY_EVENT_NOT_FOUND: {
    title: 'Evento no encontrado',
    subtitle:
      'El evento de actividad solicitado no existe. Verifica el identificador e intenta nuevamente.',
  },
  CAPABILITY_PERMISSION_NOT_FOUND: {
    title: 'Permiso no encontrado',
    subtitle:
      'El permiso de capacidad indicado no existe. Verifica el identificador e intenta nuevamente.',
  },
  CAPABILITY_ROLE_COMPANY_MISMATCH: {
    title: 'Rol de otra empresa',
    subtitle:
      'Al menos un rol no pertenece a las empresas seleccionadas. Revisa los roles.',
  },
  CAPABILITY_ROLE_NOT_FOUND: {
    title: 'Rol no encontrado',
    subtitle:
      'El rol de capacidad indicado no existe. Verifica el identificador e intentalo nuevamente.',
  },
  COMPANY_COLLECTOR_IDENTIFIER_DUPLICATED: {
    title: 'Codigo duplicado',
    subtitle:
      'Ya existe un identificador con ese codigo para este cobrador. Usa un codigo diferente.',
  },
  COMPANY_COLLECTOR_IDENTIFIER_NOT_FOUND: {
    title: 'Identificador no encontrado',
    subtitle:
      'El identificador del cobrador no existe. Verifica el registro seleccionado e intenta nuevamente.',
  },
  COMPANY_COLLECTOR_MERCHANT_DUPLICATED: {
    title: 'Codigo de comercio duplicado',
    subtitle:
      'Ya existe un comercio con ese merchantCode. Usa un codigo distinto.',
  },
  COMPANY_COLLECTOR_MERCHANT_NOT_FOUND: {
    title: 'Comercio no encontrado',
    subtitle:
      'El comercio del recaudador no existe. Verifica el identificador e intenta nuevamente.',
  },
  COMPANY_COLLECTOR_GROUP_NOT_FOUND: {
    title: 'Grupo no encontrado',
    subtitle:
      'El grupo de códigos de comercio no existe. Actualiza la lista e intenta nuevamente.',
  },
  GROUP_CODE_TAKEN: {
    title: 'Código de grupo duplicado',
    subtitle:
      'Ya existe un grupo con ese código para este procesador. Usa un código distinto.',
  },
  GROUP_MERCHANT_INVALID: {
    title: 'Comercio inválido',
    subtitle:
      'Uno de los comercios no existe o no pertenece a este procesador. Actualiza la lista e intenta nuevamente.',
  },
  GROUP_IN_USE_BY_FLOW: {
    title: 'Grupo en uso',
    subtitle:
      'El grupo está asignado a un flujo de conciliación. Quítalo del flujo antes de eliminarlo.',
  },
  FLOW_DEFINITION_NOT_FOUND: {
    title: 'Flujo no encontrado',
    subtitle:
      'El flujo de conciliación no existe. Actualiza la lista e intenta nuevamente.',
  },
  FLOW_GROUP_TAKEN: {
    title: 'Grupo en otro flujo',
    subtitle:
      'Uno de los grupos ya está en uso en otro flujo de este procesador. Quítalo de ese flujo o elige otro grupo.',
  },
  FLOW_GROUP_INVALID: {
    title: 'Grupo inválido',
    subtitle:
      'Uno de los grupos no existe o no pertenece a este procesador. Actualiza la lista e intenta nuevamente.',
  },
  FLOW_STEPS_INVALID_STRUCTURE: {
    title: 'Estructura de pasos inválida',
    subtitle:
      'El orden de los pasos no es válido: los datos se complementan antes de validar y conciliar es el último paso.',
  },
  FLOW_STEPS_CONFLICT: {
    title: 'Edición simultánea',
    subtitle:
      'El flujo fue modificado por otra edición. Recarga el builder y vuelve a guardar.',
  },
  FLOW_PUBLISH_ISSUES: {
    title: 'El flujo está incompleto',
    subtitle: 'Revisa la lista de pendientes en el builder para activarlo.',
  },
  FLOW_PUBLISH_CONFLICT: {
    title: 'Publicación simultánea',
    subtitle:
      'El flujo acaba de ser publicado por otra edición. Recarga el builder para ver la versión vigente.',
  },
  FLOW_DEFINITION_NOT_PUBLISHED: {
    title: 'Flujo sin publicar',
    subtitle:
      'El flujo nunca se ha publicado. Complétalo y publícalo desde el builder para activarlo.',
  },
  FLOW_DEFINITION_ACTIVE: {
    title: 'Flujo activo',
    subtitle: 'Un flujo activo no se puede eliminar. Primero desactívalo.',
  },
  FLOW_DEFINITION_NOT_ACTIVE: {
    title: 'Flujo no activo',
    subtitle:
      'Solo un flujo activo se puede ejecutar. Actívalo desde el listado de flujos.',
  },
  FLOW_EXECUTION_IN_PROGRESS: {
    title: 'Corrida en curso',
    subtitle:
      'El flujo ya tiene un lote en proceso. Espera a que termine o cancélalo desde el historial.',
  },
  FLOW_EXECUTION_NOT_FOUND: {
    title: 'Lote no encontrado',
    subtitle:
      'El lote de ejecución no existe. Actualiza el historial e intenta nuevamente.',
  },
  FLOW_EXECUTION_NOT_IN_PROGRESS: {
    title: 'El lote ya terminó',
    subtitle:
      'Solo un lote en proceso se puede cancelar. Actualiza el historial para ver su estado final.',
  },
  FLOW_EXECUTION_FILE_REQUIRED: {
    title: 'Falta un archivo',
    subtitle:
      'Uno de los pasos del flujo requiere un archivo que no fue cargado. Vuelve al paso de carga.',
  },
  FLOW_EXECUTION_FILE_INVALID: {
    title: 'Archivo con formato inválido',
    subtitle:
      'Uno de los archivos no terminó su validación de formato o fue rechazado. Revisa el paso de carga.',
  },
  FLOW_EXECUTION_FILE_DUPLICATED: {
    title: 'Archivo repetido',
    subtitle:
      'El mismo archivo fue asignado a más de un paso. Carga un archivo distinto por paso.',
  },
  FLOW_EXECUTION_FILE_NOT_FOUND: {
    title: 'Archivo no encontrado',
    subtitle:
      'Uno de los archivos cargados ya no existe. Vuelve a subirlo e intenta nuevamente.',
  },
  COMPANY_COLLECTOR_NOT_FOUND: {
    title: 'Cobrador no encontrado',
    subtitle:
      'El cobrador de la empresa no existe. Verifica que el identificador enviado sea correcto e intenta nuevamente.',
  },
  COMPANY_COLLECTOR_SCHEMA_CUSTOM_FIELDS_REQUIRED: {
    title: 'Campos insuficientes',
    subtitle:
      'Un esquema personalizado requiere al menos 3 campos. Agrega los campos faltantes.',
  },
  COMPANY_COLLECTOR_SCHEMA_NOT_FOUND: {
    title: 'Esquema no encontrado',
    subtitle:
      'El esquema del colector solicitado no existe. Verifica el identificador e intenta nuevamente.',
  },
  DATE_FILTER_STRATEGY_INVALID: {
    title: 'Filtro de fecha inválido',
    subtitle:
      'El valor de dateFilterOverride no corresponde a una estrategia de fecha válida. Selecciona una estrategia permitida.',
  },
  INVALID_DOCUMENT_NUMBER: {
    title: 'Documento inválido',
    subtitle:
      'El número de documento no corresponde al tipo seleccionado. Verifica los datos.',
  },
  MANAGEMENT_FILE_DUPLICATED: {
    title: 'Archivo duplicado',
    subtitle:
      'Este archivo ya fue cargado previamente. Sube un archivo distinto o usa el existente.',
  },
  MANAGEMENT_FILE_NOT_FOUND: {
    title: 'Archivo no encontrado',
    subtitle:
      'El archivo solicitado no existe o fue eliminado. Verifica el identificador e intenta nuevamente.',
  },
  MANAGEMENT_FILE_SIZE_EXCEEDED: {
    title: 'Archivo demasiado grande',
    subtitle:
      'El archivo supera el tamaño máximo permitido. Reduce su tamaño e intenta cargarlo de nuevo.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_DUPLICATED: {
    title: 'Asiento duplicado',
    subtitle:
      'Ya existe un asiento con ese slug o nombre. Usa un nombre distinto para continuar.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_EXECUTION_ERP_ALREADY_DISPATCHED: {
    title: 'Ya enviado al ERP',
    subtitle: 'Esta ejecución ya fue enviada al ERP anteriormente.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_EXECUTION_ERP_DISPATCH_ENQUEUE_FAILED: {
    title: 'Error al encolar',
    subtitle: 'No se pudo encolar el envío al ERP. Intenta de nuevo.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_EXECUTION_FILE_MISSING: {
    title: 'Sin archivo',
    subtitle: 'La ejecución no tiene un archivo generado para enviar.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_EXECUTION_INVALID_STATUS: {
    title: 'Estado no válido',
    subtitle:
      'Solo se pueden previsualizar archivos de ejecuciones en estado SUCCESS.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_EXECUTION_NOT_FOUND: {
    title: 'Ejecución no encontrada',
    subtitle: 'No existe la ejecución de asiento indicada.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_EXECUTION_NOT_SUCCESS: {
    title: 'Estado no válido',
    subtitle:
      'Solo se pueden enviar ejecuciones en estado SUCCESS. Espera a que finalice.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_EXECUTION_NO_FILE: {
    title: 'Sin archivo',
    subtitle: 'La ejecución no tiene archivo asociado.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_EXECUTION_PROCESSING_LOCKED: {
    title: 'Ejecución en curso',
    subtitle:
      'La ejecución está en proceso y no puede modificarse. Espera a que finalice.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_INVALID_CONFIG: {
    title: 'Configuración inválida',
    subtitle:
      'La configuración del asiento no es válida. Revisa los campos y corrige los errores indicados.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_NOT_FOUND: {
    title: 'Asiento no encontrado',
    subtitle:
      'El asiento contable de reporte de gestión no existe. Verifica el identificador e inténtalo de nuevo.',
  },
  MANAGEMENT_REPORT_ACCOUNTING_TEMPLATE_MISSING: {
    title: 'Sin plantilla asociada',
    subtitle:
      'El asiento no tiene una plantilla de reporte asociada. Asocia una plantilla antes de validar.',
  },
  MANAGEMENT_REPORT_COMPANY_FORBIDDEN: {
    title: 'Reporte de otra empresa',
    subtitle: 'No puedes programar o usar un reporte de otra empresa.',
  },
  MANAGEMENT_REPORT_COMPANY_MISMATCH: {
    title: 'Plantilla de otra empresa',
    subtitle:
      'La plantilla de reporte pertenece a otra empresa. Usa una plantilla de tu propia empresa.',
  },
  MANAGEMENT_REPORT_DATE_FILTER_INVALID: {
    title: 'Filtro de fecha inválido',
    subtitle: 'La combinación de columna y default de fecha no es válida.',
  },
  MANAGEMENT_REPORT_DATE_FILTER_PAIR_REQUIRED: {
    title: 'Filtro de fecha incompleto',
    subtitle: 'Debes completar la columna y el default de fecha juntos.',
  },
  MANAGEMENT_REPORT_DUPLICATED: {
    title: 'Plantilla duplicada',
    subtitle:
      'Ya existe una plantilla con ese slug o nombre. Usa uno distinto.',
  },
  MANAGEMENT_REPORT_ENTITY_TYPE_CHANGE_CONFIG_REQUIRED: {
    title: 'Reenvía la configuración',
    subtitle:
      'Al cambiar el tipo de entidad debes reenviar o limpiar todos los campos de configuración.',
  },
  MANAGEMENT_REPORT_ENTITY_TYPE_UNSUPPORTED: {
    title: 'Tipo no soportado',
    subtitle: 'El tipo de entidad seleccionado no está soportado.',
  },
  MANAGEMENT_REPORT_EXECUTION_COMPANY_MISMATCH: {
    title: 'Ejecución de otra empresa',
    subtitle: 'La ejecución de reporte pertenece a otra empresa.',
  },
  MANAGEMENT_REPORT_EXECUTION_INVALID_STATUS: {
    title: 'Estado no válido',
    subtitle:
      'Solo se pueden previsualizar o descargar archivos de ejecuciones en estado SUCCESS. Espera a que la ejecución finalice correctamente.',
  },
  MANAGEMENT_REPORT_EXECUTION_NOT_FOUND: {
    title: 'Ejecución no encontrada',
    subtitle:
      'No existe la ejecución de reporte solicitada. Verifica el identificador e intenta nuevamente.',
  },
  MANAGEMENT_REPORT_EXECUTION_NOT_SUCCESS: {
    title: 'Estado no válido',
    subtitle:
      'Solo se pueden previsualizar o descargar ejecuciones en estado SUCCESS.',
  },
  MANAGEMENT_REPORT_EXECUTION_NO_FILE: {
    title: 'Sin archivo',
    subtitle:
      'La ejecución no generó ningún archivo asociado. Vuelve a ejecutar el reporte para generarlo.',
  },
  MANAGEMENT_REPORT_EXECUTION_PROCESSING: {
    title: 'Ejecución en curso',
    subtitle:
      'La ejecución está en estado PROCESSING y no se puede eliminar. Espera a que finalice para intentarlo.',
  },
  MANAGEMENT_REPORT_EXECUTION_TEMPLATE_MISMATCH: {
    title: 'Plantilla no coincide',
    subtitle: 'La ejecución no corresponde a la plantilla indicada.',
  },
  MANAGEMENT_REPORT_EXECUTION_TEMPLATE_MISSING: {
    title: 'Sin plantilla',
    subtitle: 'La ejecución no tiene una plantilla de reporte asociada.',
  },
  MANAGEMENT_REPORT_INVALID_DATE_COLUMN: {
    title: 'Columna de fecha inválida',
    subtitle:
      'La columna de fecha no es válida para el tipo de entidad seleccionado.',
  },
  MANAGEMENT_REPORT_NOT_FOUND: {
    title: 'Plantilla no encontrada',
    subtitle:
      'La plantilla de reporte indicada no existe. Selecciona una plantilla válida.',
  },
  MANAGEMENT_SCHEDULE_DUPLICATED: {
    title: 'Programación duplicada',
    subtitle: 'Ya existe una programación para este reporte.',
  },
  MANAGEMENT_SCHEDULE_NOT_FOUND: {
    title: 'Programación no encontrada',
    subtitle:
      'No se encontró la programación de gestión indicada. Verifica que el identificador de la programación sea válido e intenta nuevamente.',
  },
  MANAGEMENT_SCHEDULE_TARGET_TYPE_UNSUPPORTED: {
    title: 'Tipo no soportado',
    subtitle: 'El tipo de objetivo de la programación no está soportado.',
  },
  MANAGEMENT_VERSION_DUPLICATED: {
    title: 'Versión duplicada',
    subtitle:
      'Ya existe una versión para esa aplicación. Verifica la aplicación seleccionada o edita la versión existente.',
  },
  MANAGEMENT_VERSION_NOT_FOUND: {
    title: 'Versión no encontrada',
    subtitle:
      'No se encontró la versión indicada. Verifica el identificador e inténtalo de nuevo.',
  },
  MANAGEMENT_WEBHOOK_EVENT_NOT_FOUND: {
    title: 'Evento no encontrado',
    subtitle: 'El evento de webhook indicado no existe.',
  },
  MANAGEMENT_WEBHOOK_NOT_FOUND: {
    title: 'Webhook no encontrado',
    subtitle: 'El webhook indicado no existe. Verifica el identificador.',
  },
  OPERATION_FORBIDDEN: {
    title: 'Sin permiso',
    subtitle: 'No tienes permiso para realizar esta acción.',
  },
  ORGANIZATION_COMPANY_COLLECTOR_DEPRECATED: {
    title: 'Acción no disponible',
    subtitle:
      'Gestiona los recaudadores desde las secciones de identificadores o comercios.',
  },
  ORGANIZATION_COMPANY_DUPLICATED: {
    title: 'Empresa duplicada',
    subtitle:
      'Ya existe una empresa con esos datos (documento o razón social). Usa datos distintos.',
  },
  ORGANIZATION_COMPANY_INVALID: {
    title: 'Empresa no válida',
    subtitle:
      'La empresa activa no es válida para tu usuario. Selecciona una empresa a la que tengas acceso.',
  },
  ORGANIZATION_COMPANY_NOT_FOUND: {
    title: 'Empresa no encontrada',
    subtitle:
      'No se encontró la empresa asociada. Verifica la empresa seleccionada o contacta al administrador.',
  },
  ORGANIZATION_COMPANY_PAYMENT_METHOD_REQUIRED: {
    title: 'Medio de pago requerido',
    subtitle: 'Debes habilitar al menos un medio de pago para la empresa.',
  },
  ORGANIZATION_HOLDING_DUPLICATED: {
    title: 'Holding duplicado',
    subtitle:
      'Ya existe un holding con ese nombre o subdominio. Usa uno distinto.',
  },
  ORGANIZATION_HOLDING_NOT_FOUND: {
    title: 'Holding no encontrado',
    subtitle:
      'No existe un holding para este subdominio o está inactivo. Verifica la dirección de acceso.',
  },
  ORGANIZATION_HOLDING_SUBDOMAIN_RESERVED: {
    title: 'Subdominio reservado',
    subtitle: 'Ese subdominio está reservado por el sistema. Elige otro.',
  },
  ORGANIZATION_INTEGRATION_NOT_FOUND: {
    title: 'Integración no encontrada',
    subtitle: 'La integración solicitada no existe. Verifica la configuración.',
  },
  ORGANIZATION_STORE_ASSIGN_FORBIDDEN: {
    title: 'Sin permiso para asignar',
    subtitle: 'No tienes acceso para asignar usuarios a esta tienda.',
  },
  ORGANIZATION_STORE_CODE_REQUIRED: {
    title: 'Código requerido',
    subtitle: 'El código de la tienda es obligatorio.',
  },
  ORGANIZATION_STORE_COMPANY_MISMATCH: {
    title: 'Tienda de otra empresa',
    subtitle: 'La tienda no pertenece a la empresa indicada.',
  },
  ORGANIZATION_STORE_DUPLICATE_USERS: {
    title: 'Usuarios duplicados',
    subtitle: 'No se permiten usuarios repetidos en la asignación.',
  },
  ORGANIZATION_STORE_FORBIDDEN: {
    title: 'Sin acceso a la tienda',
    subtitle: 'No tienes acceso a esta tienda.',
  },
  ORGANIZATION_STORE_NOT_FOUND: {
    title: 'Tienda no encontrada',
    subtitle:
      'La tienda indicada no existe. Verifica la selección e intenta nuevamente.',
  },
  ORGANIZATION_STORE_PIN_INVALID: {
    title: 'PIN inválido',
    subtitle: 'El PIN ingresado no es válido.',
  },
  ORGANIZATION_STORE_UNASSIGN_FORBIDDEN: {
    title: 'Sin permiso para desasignar',
    subtitle: 'No tienes acceso para quitar usuarios de esta tienda.',
  },
  ORGANIZATION_STORE_USERS_FOREIGN: {
    title: 'Usuarios de otra empresa',
    subtitle: 'Algunos usuarios no pertenecen a la empresa de la tienda.',
  },
  ORGANIZATION_STORE_USER_NOT_ASSIGNED: {
    title: 'Usuario no asignado',
    subtitle: 'El usuario no está asignado actualmente a esta tienda.',
  },
  PASSWORD_REQUIRED: {
    title: 'Contraseña requerida',
    subtitle: 'Debes ingresar tu contraseña para acceder a este sistema.',
  },
  PAYMENT_COLLECTOR_DUPLICATED: {
    title: 'Recaudador duplicado',
    subtitle: 'Ya existe un recaudador con ese código. Usa uno distinto.',
  },
  PAYMENT_COLLECTOR_NOT_FOUND: {
    title: 'Recaudador no encontrado',
    subtitle: 'El recaudador indicado no existe. Verifica la selección.',
  },
  PIN_REQUIRED: {
    title: 'PIN requerido',
    subtitle: 'Debes ingresar tu PIN para acceder desde la aplicación.',
  },
  PURCHASE_CHANNEL_NOT_FOUND: {
    title: 'Canal no encontrado',
    subtitle: 'El canal de compra indicado no existe. Verifica la selección.',
  },
  PURCHASE_DISPUTE_NOT_FOUND: {
    title: 'Disputa no encontrada',
    subtitle: 'La disputa indicada no existe. Verifica el identificador.',
  },
  PURCHASE_FILE_FORMAT_NOT_CONFIGURED: {
    title: 'Formato no configurado',
    subtitle:
      'El recaudador no tiene formatos configurados para este tipo de archivo.',
  },
  PURCHASE_FILE_FORMAT_UNSUPPORTED: {
    title: 'Formato no soportado',
    subtitle:
      'El formato del archivo no es compatible con el recaudador para este tipo.',
  },
  PURCHASE_FILE_INVALID_STATE: {
    title: 'Estado no válido',
    subtitle: 'El archivo no puede reprocesarse en su estado actual.',
  },
  PURCHASE_FILE_INVALID_TYPE: {
    title: 'Tipo de archivo inválido',
    subtitle: 'El tipo de archivo no coincide con el esperado.',
  },
  PURCHASE_FILE_MISSING_COLUMNS: {
    title: 'Columnas faltantes',
    subtitle: 'Al archivo le faltan columnas requeridas. Revisa la plantilla.',
  },
  PURCHASE_FILE_NO_ERROR_FILE: {
    title: 'Sin archivo de errores',
    subtitle: 'Este archivo no tiene un archivo de errores disponible.',
  },
  PURCHASE_FILE_SIZE_EXCEEDED: {
    title: 'Archivo demasiado grande',
    subtitle: 'El archivo supera el tamaño máximo permitido. Reduce su tamaño.',
  },
  PURCHASE_ITEM_NOT_FOUND: {
    title: 'Ítem no encontrado',
    subtitle: 'El ítem de compra indicado no existe.',
  },
  PURCHASE_OPERATION_ALREADY_RECONCILED: {
    title: 'Operación ya conciliada',
    subtitle: 'Esta operación ya fue conciliada.',
  },
  PURCHASE_OPERATION_NOT_FOUND: {
    title: 'Operación no encontrada',
    subtitle: 'La operación de compra indicada no existe.',
  },
  PURCHASE_OPERATION_TRANSACTION_REQUIRED: {
    title: 'Transacción requerida',
    subtitle:
      'Este tipo de operación requiere una transacción para conciliarse.',
  },
  PURCHASE_ORDER_CANCEL_APPROVED: {
    title: 'No se puede cancelar',
    subtitle: 'No puedes cancelar una orden con transacciones aprobadas.',
  },
  PURCHASE_ORDER_DUPLICATED: {
    title: 'Orden duplicada',
    subtitle: 'Ya existe una orden de compra con ese código.',
  },
  PURCHASE_ORDER_FORBIDDEN: {
    title: 'Sin acceso a la orden',
    subtitle: 'No tienes acceso a esta orden de compra.',
  },
  PURCHASE_ORDER_HISTORY_NOT_FOUND: {
    title: 'Historial no encontrado',
    subtitle: 'No se encontró el historial de la orden indicada.',
  },
  PURCHASE_ORDER_NOT_FOUND: {
    title: 'Orden no encontrada',
    subtitle:
      'La orden de compra indicada no existe. Verifica el identificador.',
  },
  PURCHASE_ORDER_STORE_CODE_REQUIRED: {
    title: 'Datos requeridos',
    subtitle: 'Se requieren el código de tienda y el código de la orden.',
  },
  PURCHASE_ORDER_UPDATE_APPROVED: {
    title: 'No se puede actualizar',
    subtitle: 'No puedes actualizar una orden con transacciones aprobadas.',
  },
  PURCHASE_TRANSACTION_CARD_FIELDS_FORBIDDEN: {
    title: 'Datos no permitidos',
    subtitle: 'No debes enviar datos de tarjeta en pagos en efectivo o cupón.',
  },
  PURCHASE_TRANSACTION_CARD_FIELDS_REQUIRED: {
    title: 'Datos de tarjeta requeridos',
    subtitle: 'Faltan datos obligatorios para el pago con tarjeta.',
  },
  PURCHASE_TRANSACTION_COLLECTOR_REQUIRED: {
    title: 'Recaudador requerido',
    subtitle:
      'Debes indicar el procesador de pago para transacciones con tarjeta.',
  },
  PURCHASE_TRANSACTION_HISTORY_NOT_FOUND: {
    title: 'Historial no encontrado',
    subtitle: 'No se encontró el historial de la transacción indicada.',
  },
  PURCHASE_TRANSACTION_INVALID_AMOUNT: {
    title: 'Monto inválido',
    subtitle: 'El monto debe ser positivo para ventas y autorizaciones.',
  },
  PURCHASE_TRANSACTION_NOT_FOUND: {
    title: 'Transacción no encontrada',
    subtitle: 'La transacción indicada no existe. Verifica el identificador.',
  },
  PURCHASE_TRANSACTION_TOTAL_MISMATCH: {
    title: 'Montos no coinciden',
    subtitle: 'El total pagado no coincide con el monto más la propina.',
  },
  PURCHASE_VOUCHER_TEMPLATE_MISSING: {
    title: 'Plantilla no configurada',
    subtitle: 'No hay una plantilla de comprobante configurada para este tipo.',
  },
  RECOVERY_EMAIL_COOLDOWN: {
    title: 'Espera un momento',
    subtitle:
      'Ya enviamos un correo de recuperación recientemente. Espera unos minutos antes de solicitar otro.',
  },
  RESOURCE_COMPANY_FORBIDDEN: {
    title: 'Acción no permitida',
    subtitle:
      'No puedes realizar esta acción sobre un recurso que pertenece a otra empresa.',
  },
  RESOURCE_NOT_FOUND: {
    title: 'Recurso no encontrado',
    subtitle:
      'El recurso solicitado no existe. Verifica el identificador e intenta nuevamente.',
  },
  USER_FORBIDDEN: {
    title: 'Acceso denegado',
    subtitle:
      'Necesitas permisos de administrador o administrador de empresa para realizar esta accion.',
  },
  USER_PERSON_ADMIN_DOMAIN_REQUIRED: {
    title: 'Ingresa por el dominio admin',
    subtitle:
      'Los usuarios Kipey deben iniciar sesión desde el dominio administrativo, no desde un subdominio de holding.',
  },
  USER_PERSON_ADMIN_REQUIRED: {
    title: 'Permiso de administrador',
    subtitle:
      'Debes ser administrador o administrador de empresa para esta acción.',
  },
  USER_PERSON_COMPANY_ADMIN_REQUIRED: {
    title: 'Permiso de administrador',
    subtitle: 'Debes ser administrador de empresa para esta acción.',
  },
  USER_PERSON_DUPLICATED: {
    title: 'Usuario duplicado',
    subtitle: 'Ya existe un usuario con esos datos. Usa datos distintos.',
  },
  USER_PERSON_EXTERNAL_CODE_DUPLICATED: {
    title: 'Código externo en uso',
    subtitle:
      'Ese código externo ya está en uso por otro usuario de la empresa.',
  },
  USER_PERSON_HOLDING_CONFLICT: {
    title: 'Empresas de holdings distintos',
    subtitle:
      'Un usuario no puede pertenecer a empresas de dos holdings diferentes.',
  },
  USER_PERSON_HOLDING_MIX: {
    title: 'No se puede mezclar empresas',
    subtitle:
      'Un usuario no puede combinar empresas de un holding con empresas sin holding.',
  },
  USER_PERSON_HOLDING_SUBDOMAIN_REQUIRED: {
    title: 'Ingresa por tu subdominio',
    subtitle:
      'Debes iniciar sesión desde el subdominio de tu holding. Usa la URL asignada a tu organización.',
  },
  USER_PERSON_INVALID_CREDENTIALS: {
    title: 'Credenciales inválidas',
    subtitle:
      'El usuario o la contraseña son incorrectos. Verifica tus datos e intenta nuevamente.',
  },
  USER_PERSON_NOT_FOUND: {
    title: 'Usuario no encontrado',
    subtitle:
      'No existe un usuario con esas credenciales. Verifica el usuario ingresado e intenta nuevamente.',
  },
  USER_PERSON_ROLES_COMPANIES_REQUIRED: {
    title: 'Roles y empresas requeridos',
    subtitle: 'Debes asignar al menos un rol y una empresa.',
  },
  USER_PERSON_SYSTEM_FORBIDDEN: {
    title: 'Acceso denegado',
    subtitle:
      'El usuario no tiene acceso a este sistema. Solicita al administrador que le asigne los permisos correspondientes.',
  },
  USER_PERSON_SYSTEM_TYPE_REQUIRED: {
    title: 'Sistema requerido',
    subtitle: 'Debes indicar al menos un tipo de sistema.',
  },
};
